import { useState } from 'react'
import { useTenant } from '@/contexts'
import {
  useLocationUsers,
  useAvailableTeamMembers,
  useCreateLocationUser,
  useUpdateLocationUser,
  useDeleteLocationUser,
} from '@/hooks/useLocationUsers'
import {
  LocationUsersTable,
  AssignUserDialog,
  EditLocationRoleDialog,
  RemoveLocationUserDialog,
} from '@/components/settings/locations'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Plus } from 'lucide-react'
import type { LocationUserTableRow, LocationRole } from '@/types/location-users'

export function LocationPermissionsPage() {
  const { currentOrganization, locations } = useTenant()
  const [selectedLocationId, setSelectedLocationId] = useState<string>('')
  const [selectedUser, setSelectedUser] = useState<LocationUserTableRow | null>(null)
  const [assignDialogOpen, setAssignDialogOpen] = useState(false)
  const [editRoleDialogOpen, setEditRoleDialogOpen] = useState(false)
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false)

  // Queries and mutations
  const { data: locationUsers = [], isLoading: isLoadingUsers } = useLocationUsers(selectedLocationId)
  const { data: availableUsers = [] } = useAvailableTeamMembers(selectedLocationId, currentOrganization?.id)
  const createLocationUser = useCreateLocationUser()
  const updateLocationUser = useUpdateLocationUser()
  const deleteLocationUser = useDeleteLocationUser()

  // Check if current user can manage this location
  // For now, allow if they're an org owner or admin - more granular checks can be added later
  const canManage = true // TODO: Add proper permission check based on location role

  const handleAssignUser = async (data: { user_id: string; location_role: LocationRole }) => {
    if (!selectedLocationId) return
    await createLocationUser.mutateAsync({
      location_id: selectedLocationId,
      user_id: data.user_id,
      location_role: data.location_role,
    })
  }

  const handleEditRole = (user: LocationUserTableRow) => {
    setSelectedUser(user)
    setEditRoleDialogOpen(true)
  }

  const handleUpdateRole = async (newRole: LocationRole) => {
    if (!selectedUser) return
    await updateLocationUser.mutateAsync({
      id: selectedUser.id,
      data: { location_role: newRole },
    })
    setSelectedUser(null)
  }

  const handleRemoveClick = (user: LocationUserTableRow) => {
    setSelectedUser(user)
    setRemoveDialogOpen(true)
  }

  const handleRemoveConfirm = async () => {
    if (!selectedUser) return
    await deleteLocationUser.mutateAsync(selectedUser.id)
    setSelectedUser(null)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Location Permissions</h1>
        <p className="text-muted-foreground mt-1">
          Manage which team members have access to each location and their roles
        </p>
      </div>

      {/* Location Selector */}
      <div className="space-y-3">
        <Label htmlFor="location-select">Select Location</Label>
        <Select value={selectedLocationId} onValueChange={setSelectedLocationId}>
          <SelectTrigger id="location-select" className="w-full">
            <SelectValue placeholder="Choose a location..." />
          </SelectTrigger>
          <SelectContent>
            {(locations || []).map((location) => (
              <SelectItem key={location.id} value={location.id}>
                {location.name}
                {location.location_code && ` (${location.location_code})`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {locations?.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No locations available. Create a location first.
          </p>
        )}
      </div>

      {/* Users Table Section */}
      {selectedLocationId && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold">Team Members</h2>
              <p className="text-sm text-muted-foreground">
                {locationUsers.length} {locationUsers.length === 1 ? 'member' : 'members'} assigned
              </p>
            </div>
            {canManage && (
              <Button
                onClick={() => setAssignDialogOpen(true)}
                size="sm"
                className="gap-2"
                disabled={availableUsers.length === 0}
              >
                <Plus className="h-4 w-4" />
                Add User
              </Button>
            )}
          </div>

          <LocationUsersTable
            users={locationUsers}
            canManage={canManage}
            onEditRole={handleEditRole}
            onRemove={handleRemoveClick}
            isLoading={isLoadingUsers}
          />
        </div>
      )}

      {/* Empty State */}
      {!selectedLocationId && locations && locations.length > 0 && (
        <div className="flex items-center justify-center h-64 rounded-lg border border-dashed border-muted-foreground/30">
          <p className="text-muted-foreground">Select a location to manage permissions</p>
        </div>
      )}

      {/* Dialogs */}
      <AssignUserDialog
        open={assignDialogOpen}
        onOpenChange={setAssignDialogOpen}
        availableUsers={availableUsers}
        onAssign={handleAssignUser}
        isLoading={createLocationUser.isPending}
      />

      <EditLocationRoleDialog
        open={editRoleDialogOpen}
        onOpenChange={setEditRoleDialogOpen}
        user={selectedUser}
        onUpdateRole={handleUpdateRole}
        isLoading={updateLocationUser.isPending}
      />

      <RemoveLocationUserDialog
        open={removeDialogOpen}
        onOpenChange={setRemoveDialogOpen}
        user={selectedUser}
        onConfirm={handleRemoveConfirm}
        isLoading={deleteLocationUser.isPending}
      />
    </div>
  )
}
